import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import EditFieldModal from "./EditFieldModal";
import "./FormCanvas.css";
import { RiDeleteBin4Fill } from "react-icons/ri";
import { MdEditSquare } from "react-icons/md";
import {
  InputLabel,
  InputType,
  SelectBorder,
} from "../../../../Global/GlobalFormElement";
import { useDispatch, useSelector } from "react-redux";
import { FiCopy } from "react-icons/fi";
import {
  resetEntryState,
  updateEntryFieldPayload,
} from "../../../../Redux/Users/action";

const FormCanvas = ({
  fields = [], // Default to empty array
  updateField,
  removeField,
  disableRequiredCheckbox,
  addedFields = [], // Default to empty array
  formSchema,
  copiedFields = [], // Default to empty array
  copyField,
  removeFieldFromCopied,
  updateAddedField,
  updateCopiedField,
  copyNormalField,
  handleDragStart,
  handleDragEnd,
  handleDrop,
  setIsDragging,
  isDragging,
  setDragOverIndex,
  dragOverIndex,
  handleDragOver,
  handleDragLeave,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [conditionFieldName, setConditionFieldName] = useState(null);
  const [fieldType, setFieldType] = useState("");
  const containerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  
  const entryFieldPayload = useSelector((state) => state.entry.fieldPayload);
  const dispatch = useDispatch();
  
  const countries = useMemo(
    () => [
      "United States",
      "Canada",
      "United Kingdom",
      "Australia",
      "India",
      "Germany",
      "France",
      "Italy",
      "Spain",
      "Mexico",
      "Japan",
      "South Korea",
      "Brazil",
      "China",
      "South Africa",
      "Russia",
      "Netherlands",
      "Sweden",
      "Switzerland",
      "Belgium",
    ],
    [],
  );

  const fieldLabels = useMemo(
    () => (fields || []).map((field) => field.label),
    [fields],
  );

  const dataNames = useMemo(
    () => (fields || []).map((field) => field.dataName),
    [fields],
  );

  // Auto-scroll during drag
// Auto-scroll during drag - optimized
const handleGlobalDragOver = useCallback((e) => {
  if (!containerRef.current || !isDragging) return;
  
  const container = containerRef.current;
  const rect = container.getBoundingClientRect();
  const scrollSpeed = 12; // Reduced for smoother scroll
  const threshold = 80; // Slightly reduced threshold
  
  const mouseY = e.clientY;
  const distanceFromTop = mouseY - rect.top;
  const distanceFromBottom = rect.bottom - mouseY;
  
  // Clear existing interval
  if (scrollIntervalRef.current) {
    clearInterval(scrollIntervalRef.current);
    scrollIntervalRef.current = null;
  }
  
  // Scroll up when near top
  if (distanceFromTop < threshold && distanceFromTop > 0) {
    scrollIntervalRef.current = setInterval(() => {
      container.scrollTop -= scrollSpeed;
    }, 16); // 60fps
  }
  // Scroll down when near bottom
  else if (distanceFromBottom < threshold && distanceFromBottom > 0) {
    scrollIntervalRef.current = setInterval(() => {
      container.scrollTop += scrollSpeed;
    }, 16);
  }
}, [isDragging]);
  const handleGlobalDragEnd = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('dragover', handleGlobalDragOver);
      window.addEventListener('dragend', handleGlobalDragEnd);
    }
    
    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('dragend', handleGlobalDragEnd);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, [isDragging, handleGlobalDragOver, handleGlobalDragEnd]);

  const openModal = useCallback(
    (field, type) => {
      if (!field) return;
      
      setCurrentField(field);
      setFieldType(type);

      let conditionFieldName = null;

      if (field.requiredCondition && field.requiredFieldValue) {
        const conditionField = (fields || []).find(
          (f) => f.dataName === field.requiredFieldValue,
        );
        if (conditionField) {
          conditionFieldName = conditionField.dataName;
        }
      }

      if (field.visibleCondition && field.visibleFieldValue) {
        const conditionField = (fields || []).find(
          (f) => f.dataName === field.visibleFieldValue,
        );
        if (conditionField) {
          conditionFieldName = conditionField.dataName;
        }
      }

      setIsModalOpen(true);
      setConditionFieldName(conditionFieldName);
    },
    [fields],
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentField(null);
  };

  const handleRequiredChange = (index) => {
    if (!addedFields || !Array.isArray(addedFields)) return;
    
    const updatedFields = [...addedFields];
    const field = updatedFields[index];

    if (!field) return;

    const updatedField = {
      ...field,
      required: !field.required,
    };

    updatedFields[index] = updatedField;

    dispatch(
      updateEntryFieldPayload(field.dataName, {
        required: updatedField.required,
      }),
    );
    updateAddedField(updatedFields);
  };

  const handleVisibleChange = (index) => {
    if (!addedFields || !Array.isArray(addedFields)) return;
    
    const updatedFields = [...addedFields];
    const field = updatedFields[index];

    if (!field) return;

    const updatedField = {
      ...field,
      visible: !field.visible,
    };

    updatedFields[index] = updatedField;

    dispatch(
      updateEntryFieldPayload(field.dataName, {
        visible: updatedField.visible,
      }),
    );
    updateAddedField(updatedFields);
  };

  const handleSave = (updatedField) => {
    try {
      if (!fields || !Array.isArray(fields)) return;
      
      const updatedFields = fields.map((field) =>
        field.dataName === updatedField.dataName
          ? { ...field, ...updatedField }
          : field,
      );
      updateField(updatedFields);
      closeModal();
    } catch (error) {
      console.error("Error saving field:", error);
    }
  };

  const handleFileChange = (e, fieldIndex) => {
    const file = e.target.files[0];
    if (file) {
      try {
        if (!fields || !Array.isArray(fields)) return;
        
        const updatedFields = [...fields];
        updatedFields[fieldIndex].value = file;
        updateField(updatedFields);
      } catch (error) {
        console.error("Error handling file change:", error);
      }
    }
  };

  const handleRequiredToggle = (fieldIndex) => {
    if (!fields || !Array.isArray(fields)) return;
    
    const updatedFields = [...fields];
    const field = updatedFields[fieldIndex];
    if (!field) return;
    
    if (!field.isDefault) {
      if (field.requiredCondition && field.requiredFieldValue) {
        const conditionField = (fields || []).find(
          (f) => f.dataName === field.requiredFieldValue,
        );
        field.isConditionMet =
          conditionField && conditionField.value === field.requiredFieldValue;
        if (field.isConditionMet) {
          field.required = !field.required;
          updateField(updatedFields);
        } else {
          alert(`${field.label} is required based on the selected condition.`);
        }
      } else {
        field.isConditionMet = true;
        field.required = !field.required;
        updateField(updatedFields);
      }
    }
  };

  const handleVisibleToggle = (fieldIndex) => {
    if (!fields || !Array.isArray(fields)) return;
    
    const updatedFields = [...fields];
    const field = updatedFields[fieldIndex];
    if (!field) return;
    
    if (field.visibleCondition && field.visibleFieldValue) {
      const conditionField = (fields || []).find(
        (f) => f.dataName === field.visibleFieldValue,
      );
      field.isConditionMet =
        conditionField && conditionField.value === field.visibleFieldValue;
      if (field.isConditionMet) {
        field.visible = !field.visible;
        updateField(updatedFields);
      } else {
        alert(`${field.label} is not visible based on the selected condition.`);
      }
    } else {
      field.isConditionMet = true;
      field.visible = !field.visible;
      updateField(updatedFields);
    }
  };

  const handleFieldChange = useCallback(
    (index, value) => {
      if (!fields || !Array.isArray(fields)) return;
      
      const updatedFields = [...fields];
      updatedFields[index].value = value;
      updateField(updatedFields);

      const copiedField = updatedFields.find(
        (field) => field.dataName === `${updatedFields[index].dataName}_copy`,
      );
      if (copiedField) {
        copiedField.value = value;
        updateField(updatedFields);
      }
    },
    [fields, updateField],
  );

  const TextField = ({ field, index, handleFieldChange }) => (
    <InputType
      type="text"
      placeholder={field?.label || ""}
      value={field?.value || ""}
      onChange={handleChange}
    />
  );

  const renderField = (field, index, fieldType) => {
    if (!field) return null;
    
    const fieldProps = {
      style: { width: field.fieldSize },
      value: field.value || "",
      onChange: { handleChange },
      ariaLabel: field.label,
      disabled: true,
      readOnly: true,
    };
    
    switch (field.type) {
      case "text":
        return (
          <TextField
            field={field}
            {...fieldProps}
            type="text"
            index={index}
            handleFieldChange={handleChange}
            fieldType={fieldType}
            placeholder={
              (entryFieldPayload &&
                entryFieldPayload.find(
                  (item) => item?.dataName === field.dataName,
                )?.label) ||
              field.label
            }
            name={field.dataName}
          />
        );

      case "email":
        return (
          <InputType
            type="email"
            placeholder={
              (entryFieldPayload &&
                entryFieldPayload.find(
                  (item) => item?.dataName === field.dataName,
                )?.label) ||
              field.label
            }
            {...fieldProps}
            name={field.dataName}
          />
        );

      case "tel":
        return (
          <InputType
            type="tel"
            placeholder={
              (entryFieldPayload &&
                entryFieldPayload.find(
                  (item) => item?.dataName === field.dataName,
                )?.label) ||
              field.label
            }
            {...fieldProps}
            name={field.dataName}
          />
        );

      case "date":
        return (
          <InputType
            type="date"
            placeholder={
              (entryFieldPayload &&
                entryFieldPayload.find(
                  (item) => item?.dataName === field.dataName,
                )?.label) ||
              field.label
            }
            {...fieldProps}
            name={field.dataName}
          />
        );

      case "textarea":
        return (
          <textarea
            placeholder={
              (entryFieldPayload &&
                entryFieldPayload.find(
                  (item) => item?.dataName === field.dataName,
                )?.label) ||
              field.label
            }
            {...fieldProps}
            name={field.dataName}
          />
        );
      case "country":
        return (
          <SelectBorder
            value={field.value || ""}
            {...fieldProps}
            onChange={(e) => {
              if (!fields || !Array.isArray(fields)) return;
              const updatedFields = [...fields];
              updatedFields[index].value = e.target.value;
              updateField(updatedFields);
            }}
          >
            {countries.map((country, idx) => (
              <option key={idx} value={country}>
                {country}
              </option>
            ))}
          </SelectBorder>
        );
      case "dropdown":
        const findDropdownPayload = entryFieldPayload?.find(
          (item) => item?.dataName === field.dataName,
        );
        return (
          <SelectBorder
            value={field.value || ""}
            {...fieldProps}
            onChange={(e) => handleChange(e, fieldType)}
            aria-label={field.label}
          >
            {(findDropdownPayload
              ? findDropdownPayload.options
              : addedFields?.find((f) => f?.dataName === field.dataName)
                  ?.options || []
            ).map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </SelectBorder>
        );

      case "radio":
        const findRadioPayload = entryFieldPayload?.find(
          (item) => item?.dataName === field.dataName,
        );
        return (
          <div className="radio-btns">
            {(findRadioPayload
              ? findRadioPayload.options
              : addedFields?.find((f) => f?.dataName === field.dataName)
                  ?.options || []
            ).map((option, idx) => (
              <div key={idx} className="input-lbl">
                <input
                  type="radio"
                  name={field.dataName}
                  value={option}
                  checked={field.value === option}
                  onChange={(e) => handleChange(e, fieldType)}
                />
                <label>{option}</label>
              </div>
            ))}
          </div>
        );

      case "checkbox":
        const findFieldPayload = entryFieldPayload?.find(
          (item) => item?.dataName === field.dataName,
        );
        return (
          <div className="checkboxes">
            {(findFieldPayload
              ? findFieldPayload.options
              : addedFields?.find((f) => f?.dataName === field.dataName)
                  ?.options || []
            ).map((option, idx) => (
              <div key={idx} className="input-lbl">
                <input
                  type="checkbox"
                  checked={field.value?.includes(option)}
                  onChange={() => {
                    if (!fields || !Array.isArray(fields)) return;
                    const updatedFields = [...fields];
                    const fieldIndex = updatedFields.findIndex(
                      (f) => f?.dataName === field.dataName,
                    );
                    if (fieldIndex !== -1) {
                      const fieldValue = updatedFields[fieldIndex].value || [];
                      if (fieldValue.includes(option)) {
                        updatedFields[fieldIndex].value = fieldValue.filter(
                          (val) => val !== option,
                        );
                      } else {
                        updatedFields[fieldIndex].value = [
                          ...fieldValue,
                          option,
                        ];
                      }
                      updateField(updatedFields);
                    }
                  }}
                />
                <label>{option}</label>
              </div>
            ))}
          </div>
        );

      case "file":
        return (
          <input
            type="file"
            {...fieldProps}
            onChange={(e) => handleFileChange(e, index)}
          />
        );
      default:
        return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(name, "nameValue");
    console.log(value, "nameValue");
    if (!name || value === undefined) {
      console.error("Invalid input field or missing name/value");
      return;
    }

    if (!fields || !Array.isArray(fields)) return;

    const updatedFields = fields.map((field) => {
      if (field.dataName === name) {
        return { ...field, value };
      }
      return field;
    });

    updateField(updatedFields);
  };

  // Helper function to render fields with placeholder
  const renderFieldsWithPlaceholder = (fieldsArray, category, renderFunction) => {
    // Safety check - ensure fieldsArray is an array
    if (!fieldsArray || !Array.isArray(fieldsArray)) {
      return null;
    }
    
    return fieldsArray.map((field, index) => {
      if (!field) return null;
      
      return (
        <React.Fragment key={field.dataName || `field-${index}`}>
          {/* Drop placeholder above the field if dragOverIndex matches */}
          {dragOverIndex === index && (
            <div className="drop-placeholder" />
          )}
          <div
            className="field-container"
            draggable
            onDragStart={(e) => handleDragStart && handleDragStart(e, index, category)}
            onDragOver={(e) => {
              e.preventDefault();
              handleDragOver && handleDragOver(e, index);
            }}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop && handleDrop(e, index, category)}
            onDragEnd={handleDragEnd}
          >
            {renderFunction(field, index, category)}
          </div>
        </React.Fragment>
      );
    });
  };

  // Render function for fields
  const renderFieldContent = (field, index, category) => (
    <>
      <div className="field-input-container">
        <InputLabel>
          {field?.label || ""}
          {(field?.required || field?.isRequired) && (
            <span style={{ color: "red" }}> *</span>
          )}
          {field?.hoverHint && (
            <span className="hover-hint-icon" title={field.hoverHint}>
              ?
            </span>
          )}
        </InputLabel>
      </div>
      <div className="form_canvas_type_fields">
        {renderField(field, index, category)}
      </div>
      <div className="field-options-container">
        <div className="field-actions">
          <MdEditSquare
            className="form_canvas_icon"
            onClick={() => openModal(field, category)}
          />
          {category !== "fields" && (
            <>
              <RiDeleteBin4Fill
                className="form_canvas_icon"
                onClick={() => removeField && removeField(index)}
              />
              <FiCopy
                className="form_canvas_icon"
                onClick={() => copyField && copyField(index)}
              />
            </>
          )}
          {category === "fields" && field?.label !== "Category" && field?.label !== "Title of the Entry" && (
            <FiCopy
              className="form_canvas_icon"
              onClick={() => copyNormalField && copyNormalField(index)}
            />
          )}
        </div>
        <div className="required_checkbox">
          <label>Required</label>
          <input
            type="checkbox"
            checked={field?.required || field?.isRequired || false}
            onChange={(e) => handleChange(e, category)}
            disabled={disableRequiredCheckbox && field?.isDefault}
          />
        </div>
       <div className="visible_checkbox">
  <label>Visible</label>
  <input
    type="checkbox"
    checked={
      entryFieldPayload?.find(
        (item) => item?.dataName === field?.dataName
      )?.isVisibleCheckedInJudging ?? field?.isVisibleCheckedInJudging ?? true
    }
    onChange={(e) => handleChange(e, "copied")}
    disabled
  />
</div>
      </div>
    </>
  );

  // Render function for added fields
  const renderAddedFieldContent = (field, index, category) => (
    <>
      <div className="field-input-container">
        <InputLabel>
          {(entryFieldPayload &&
            entryFieldPayload.find(
              (item) => item?.dataName === field?.dataName,
            )?.label) ||
            field?.label || ""}
          {(field?.required || field?.isRequired) && (
            <span style={{ color: "red" }}> *</span>
          )}
          {(entryFieldPayload?.hoverHint ||
            entryFieldPayload?.find(
              (item) => item?.dataName === field?.dataName,
            )?.hoverHint ||
            field?.hoverHint) && (
            <span
              className="hover-hint-icon"
              title={
                entryFieldPayload?.find(
                  (item) => item?.dataName === field?.dataName,
                )?.hoverHint || field?.hoverHint || ""
              }
            >
              ?
            </span>
          )}
        </InputLabel>
      </div>
      <div className="form_canvas_type_fields">
        {renderField(field, index, "added")}
      </div>
      <div className="field-options-container">
        <div className="field-actions">
          <MdEditSquare
            className="form_canvas_icon"
            onClick={() => openModal(field, "added")}
          />
          <RiDeleteBin4Fill
            className="form_canvas_icon"
            onClick={() => removeField && removeField(index)}
          />
          <FiCopy
            className="form_canvas_icon"
            onClick={() => copyField && copyField(index)}
          />
        </div>
        <div className="required_checkbox">
          <label>Required</label>
          <input
            type="checkbox"
            checked={
              field?.required ||
              field?.isRequired ||
              entryFieldPayload?.find(
                (item) => item?.dataName === field?.dataName,
              )?.required || false
            }
            disabled
            onChange={() => handleRequiredChange(index)}
          />
        </div>
        <div className="visible_checkbox">
          <label>Visible</label>
          <input
            type="checkbox"
            checked={
              entryFieldPayload?.find(
                (item) => item?.dataName === field?.dataName,
              )
                ? entryFieldPayload.find(
                    (item) => item?.dataName === field?.dataName,
                  )?.visible
                : field?.visible || false
            }
            disabled
            onChange={() => handleVisibleChange(index)}
          />
        </div>
      </div>
    </>
  );

  // Render function for copied fields
  const renderCopiedFieldContent = (field, index, category) => (
    <>
      <div className="field-input-container">
        <InputLabel>
          {field?.label || ""}
          {(field?.required || field?.isRequired) && (
            <span style={{ color: "red" }}> *</span>
          )}
          {(entryFieldPayload?.hoverHint ||
            entryFieldPayload?.find(
              (item) => item?.dataName === field?.dataName,
            )?.hoverHint ||
            field?.hoverHint) && (
            <span
              className="hover-hint-icon"
              title={
                entryFieldPayload?.find(
                  (item) => item?.dataName === field?.dataName,
                )?.hoverHint || field?.hoverHint || ""
              }
            >
              ?
            </span>
          )}
        </InputLabel>
      </div>
      <div className="form_canvas_type_fields">
        {renderField(field, index)}
      </div>
      <div className="field-options-container">
        <div className="field-actions">
          <MdEditSquare
            className="form_canvas_icon"
            onClick={() => openModal(field, "added")}
          />
          <RiDeleteBin4Fill
            className="form_canvas_icon"
            onClick={() => removeFieldFromCopied && removeFieldFromCopied(index)}
          />
          <FiCopy
            className="form_canvas_icon"
            onClick={() => copyField && copyField(index)}
          />
        </div>
        <div className="required_checkbox">
          <label>Required</label>
          <input
            type="checkbox"
            checked={field?.required || field?.isRequired || false}
            onChange={() => handleRequiredToggle(index, "addedFields")}
            disabled
          />
        </div>
        <div className="visible_checkbox">
          <label>Visible</label>
          <input
            type="checkbox"
            checked={
              entryFieldPayload?.find(
                (item) => item?.dataName === field?.dataName,
              )
                ? entryFieldPayload.find(
                    (item) => item?.dataName === field?.dataName,
                  )?.visible
                : field?.visible || false
            }
            onChange={(e) => handleChange(e, "copied")}
            disabled
          />
        </div>
      </div>
    </>
  );

  return (
    <div 
      className="form-canvas" 
      ref={containerRef}
      // style={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      {/* Render fields with drop placeholders */}
      {renderFieldsWithPlaceholder(fields, "fields", renderFieldContent)}
      
      {/* Render added fields with drop placeholders */}
      {renderFieldsWithPlaceholder(addedFields, "addedFields", renderAddedFieldContent)}
      
      {/* Render copied fields with drop placeholders */}
      {renderFieldsWithPlaceholder(copiedFields, "copiedFields", renderCopiedFieldContent)}

      {isModalOpen && currentField && (
        <EditFieldModal
          field={currentField}
          onSave={handleSave}
          onClose={closeModal}
          fieldType={fieldType}
          fieldLabels={fieldLabels}
          dataNames={dataNames}
          conditionFieldName={conditionFieldName}
          formSchema={formSchema}
          addedFields={addedFields}
          updateAddedField={updateAddedField}
        />
      )}
    </div>
  );
};

export default FormCanvas;